/**
 * Shown instead of the app to a signed-in person with no usable firm — no
 * membership, or a suspended firm. See `src/lib/firm-access.ts`.
 */

import { signOut } from '@/lib/auth';
import { esc, setPageTitle } from '@/lib/dom';
import { signInBrand, NEUTRAL_BRAND, PRODUCT_DOMAIN } from '@/lib/host-branding';
import { authCardHeader } from '@/lib/auth-card';
import { firmAccessNotice, type FirmAccess } from '@/lib/firm-access';

export function renderFirmNotice(
  root: HTMLElement, access: Exclude<FirmAccess, { kind: 'ok' } | { kind: 'support' }>, email: string,
): void {
  const brand = signInBrand(location.hostname);
  const notice = firmAccessNotice(access);
  setPageTitle(notice.title, brand.titleBase);
  root.innerHTML = `
    <div class="auth">
      <div class="auth-card" data-firm-notice="${esc(access.kind)}">
        ${authCardHeader(brand, notice.title)}
        <p class="auth-copy">${esc(notice.body)}</p>
        ${email ? `<p class="auth-copy muted">Signed in as ${esc(email)}</p>` : ''}
        <button type="button" class="btn-primary" data-action="sign-out">Sign out</button>
      </div>
    </div>`;
  root.querySelectorAll('[data-action="sign-out"]')
    .forEach((el) => el.addEventListener('click', () => { void signOut(); }));
}

/**
 * A firm address (`<slug>.designstudiohq.com`) that no firm uses (Phase 27).
 * Neutral branding: this page must never guess whose address it is.
 */
export function renderNoSuchFirm(root: HTMLElement): void {
  setPageTitle('No firm at this address', NEUTRAL_BRAND.titleBase);
  root.innerHTML = `
    <div class="auth">
      <div class="auth-card" data-no-such-firm>
        ${authCardHeader(NEUTRAL_BRAND, 'No firm at this address')}
        <p class="auth-copy">There’s no design firm at ${esc(location.hostname)}. Check the address, or sign in at the main site.</p>
        <a class="btn-primary" href="https://app.${esc(PRODUCT_DOMAIN)}/">Go to app.${esc(PRODUCT_DOMAIN)}</a>
      </div>
    </div>`;
}
